// src/features/conta-corrente-processo/contaCorrenteProcessoSlice.js

import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { fetchContaCorrenteByFollowUpId, bloquearProcessoByFollowUpId } from './contaCorrenteProcessoAPI';

// Se estiver usando esses statuses e modes, importe-os
import {
  IDLE_STATUS,
  ALTERNATIVE_LOADING_STATUS,
  FAILURE_STATUS,
  LOADING_STATUS,
  SUCCESS_STATUS,
  PREPARING_ACTION_STATUS,
} from '../status';

import {
  CREATE_MODE,
  CONSULT_MODE,
  UPDATE_MODE,
  NONE_MODE,
  NONE_BACKGROUND_MODE,
  NONE_SUB_MODE,
  BACKGROUND_CREATE_MODE,
  SUB_CREATE_MODE,
  SUB_CONSULT_MODE,
  SUB_UPDATE_MODE,
} from '../mode';

/** Estado inicial que contempla:
 *  - status, error, response
 *  - mode (main, background, sub) [usado pelo seu pattern]
 *  - list e model (se precisar armazenar coleções e/ou um registro atual)
 *  - data (os campos específicos de conta corrente do processo)
 */
const initialState = {
  status: IDLE_STATUS,
  error: null,
  response: {
    status: null,
    message: null,
    data: null,
  },
  mode: {
    main: NONE_MODE,
    background: NONE_BACKGROUND_MODE,
    sub: NONE_SUB_MODE,
  },
  list: [],
  model: {}, // se quiser armazenar um objeto extra, caso use "setModel"
  data: {
    cliente: '',
    totalPagoPeloCliente: 0,
    totalPerda: 0,
    totalDebitos: 0,
    totalCreditos: 0,
    saldo: 0,
    debitosFaturados: 0,
    creditosFaturados: 0,
    saldoFaturado: 0,
    bloqueado: false,
    itens: [],
  },
};

export const fetchContaCorrenteProcessoByFollowUp = createAsyncThunk(
  'contaCorrenteProcesso/fetchByFollowUp',
  async (followUpId, { getState, rejectWithValue }) => {
    const currentFollowUpId = getState().contaCorrenteProcesso.data.followUpId;

    // Evita requisição redundante
    if (followUpId === currentFollowUpId) {
      return rejectWithValue('FollowUp já carregado');
    }

    try {
      const response = await fetchContaCorrenteByFollowUpId(followUpId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Erro ao buscar dados');
    }
  }
);

export const bloquearProcesso = createAsyncThunk(
  'contaCorrenteProcesso/bloquearProcesso',
  async ({ followUpId, bloqueado }, { rejectWithValue }) => {
    try {
      const { data } = await bloquearProcessoByFollowUpId({ followUpId, bloqueado });
      return data.bloqueado;           // só o boolean
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Falha no bloqueio');
    }
  }
);

const contaCorrenteProcessoSlice = createSlice({
  name: 'contaCorrenteProcesso',
  initialState: { ...initialState, error: null },
  reducers: {
    resetContaCorrenteProcesso(state) {
      state.data = { ...initialState.data }; // Reset completo
      state.status = IDLE_STATUS;
      state.error = null;
      state.response = { ...initialState.response };
    },

    changeStatusTo: (state, action) => {
      state.status = action.payload.status;
    },
    loading: (state) => {
      state.status = LOADING_STATUS;
    },
    success: (state) => {
      state.status = SUCCESS_STATUS;
    },
    failure: (state) => {
      state.status = FAILURE_STATUS;
    },
    preparingAction: (state) => {
      state.status = PREPARING_ACTION_STATUS;
    },
    resetStatus: (state) => {
      state.status = IDLE_STATUS;
    },

    changeToCreateMode: (state) => {
      state.mode.main = CREATE_MODE;
    },
    changeToConsultMode: (state) => {
      state.mode.main = CONSULT_MODE;
    },
    changeToUpdateMode: (state) => {
      state.mode.main = UPDATE_MODE;
    },
    resetMode: (state) => {
      state.mode.main = NONE_MODE;
    },

    changeToBackgroundCreateMode: (state) => {
      state.mode.background = BACKGROUND_CREATE_MODE;
    },
    resetBackgroundMode: (state) => {
      state.mode.background = NONE_BACKGROUND_MODE;
    },

    changeToSubCreateMode: (state) => {
      state.mode.sub = SUB_CREATE_MODE;
    },
    changeToSubConsultMode: (state) => {
      state.mode.sub = SUB_CONSULT_MODE;
    },
    changeToSubUpdateMode: (state) => {
      state.mode.sub = SUB_UPDATE_MODE;
    },
    resetSubMode: (state) => {
      state.mode.sub = NONE_SUB_MODE;
    },

    setModel: (state, action) => {
      state.model = action.payload;
    },
    resetModel: (state) => {
      state.model = initialState.model;
    },

    resetList: (state) => {
      state.list = [];
    },
    addToList: (state, action) => {
      state.list = [...state.list, action.payload.data];
    },
    updateOnList: (state, action) => {
      state.list = state.list.map((current) => {
        if (current.id === action.payload.data?.id) {
          return action.payload.data;
        }
        return current;
      });
    },

    setResponse: (state, action) => {
      state.response = action.payload;
    },
    clearResponse: (state) => {
      state.response = { ...initialState.response };
    },

    /** Erro */
    setError: (state, action) => {
      state.error = action.payload;
    },
    clearError: (state) => {
      state.error = initialState.error;
    },
  },
  extraReducers: (builder) => {
    builder
      // Dispara quando o thunk fetchContaCorrenteProcessoByFollowUp inicia
      .addCase(fetchContaCorrenteProcessoByFollowUp.pending, (state) => {
        state.status = ALTERNATIVE_LOADING_STATUS;
        state.error = null;
      })
      // Fulfilled: salvamos o payload em data
      .addCase(fetchContaCorrenteProcessoByFollowUp.fulfilled, (state, action) => {
        state.status = IDLE_STATUS;
        state.data = action.payload || initialState.data;
        const payload = action.payload || {};
        state.data = {
          ...initialState.data,
          ...payload,
          itens: payload.lancamentos || [],   // <<< para o grid
        };
      })
      // Rejected: guardamos o erro
      .addCase(fetchContaCorrenteProcessoByFollowUp.rejected, (state, action) => {
        state.status = FAILURE_STATUS;
        state.error = action.payload;
      });
      builder
        .addCase(bloquearProcesso.pending, (state) => {
          state.status = ALTERNATIVE_LOADING_STATUS;
          state.error = null;
        })
        .addCase(bloquearProcesso.fulfilled, (state, action) => {
          state.status = IDLE_STATUS;
          state.data.bloqueado = action.payload;   // true ou false
        })
        .addCase(bloquearProcesso.rejected, (state, action) => {
          state.status = FAILURE_STATUS;
          state.error = action.payload;
        });
  },
});

export const {
  resetContaCorrenteProcesso,
  changeStatusTo,
  loading,
  success,
  failure,
  preparingAction,
  resetStatus,

  changeToCreateMode,
  changeToConsultMode,
  changeToUpdateMode,
  resetMode,
  changeToBackgroundCreateMode,
  resetBackgroundMode,
  changeToSubCreateMode,
  changeToSubConsultMode,
  changeToSubUpdateMode,
  resetSubMode,

  setModel,
  resetModel,
  resetList,
  addToList,
  updateOnList,

  setResponse,
  clearResponse,
  setError,
  clearError,
} = contaCorrenteProcessoSlice.actions;

// Selectors

/** Dados (contaCorrente) vindos da API */
export const selectContaCorrenteProcesso = (state) =>
  state.contaCorrenteProcesso.data;

/** Se está carregando (ALTERNATIVE_LOADING_STATUS) */
export const selectLoading = (state) =>
  state.contaCorrenteProcesso.status === ALTERNATIVE_LOADING_STATUS;

/** Objeto de erro (caso ocorra) */
export const selectError = (state) => state.contaCorrenteProcesso.error;

/** Resposta (caso precise exibir message etc.) */
export const selectResponse = (state) => state.contaCorrenteProcesso.response;

/** Status atual (IDLE, LOADING, etc.) */
export const selectStatus = (state) => state.contaCorrenteProcesso.status;

export const selectBloqueado = (state) => state.contaCorrenteProcesso.data.bloqueado;

export default contaCorrenteProcessoSlice.reducer;
